import { type Customer, queryKeys } from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'

type Channel = 'sms' | 'email'

/**
 * Starts a text or email to a customer from their page (t1248), with no ticket
 * to begin from. The server opens a conversation for it, so it appears under
 * the customer's Support tickets and their reply lands there. A channel is only
 * offered when the customer can take it: a number that can receive texts, or an
 * email address.
 */
export function MessageCustomerButton({ customer }: { customer: Customer }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const canText = Boolean(customer.phone && customer.phoneSmsCapable)
  const canEmail = Boolean(customer.email)
  const [open, setOpen] = useState(false)
  const [channel, setChannel] = useState<Channel>(canText ? 'sms' : 'email')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')

  const send = useMutation({
    mutationFn: () =>
      api.post(
        `/customers/${customer.id}/messages`,
        channel === 'sms'
          ? { channel, body: body.trim() }
          : { channel, subject: subject.trim(), body: body.trim() },
      ),
    onSuccess: () => {
      toast.success(channel === 'sms' ? 'Text sent' : 'Email sent')
      void queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
      setOpen(false)
      setSubject('')
      setBody('')
    },
    onError: (error: Error) => toast.error(error.message),
  })

  if (!canText && !canEmail) return null

  const ready = body.trim().length > 0 && (channel === 'sms' || subject.trim().length > 0)

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          setChannel(canText ? 'sms' : 'email')
          setOpen(true)
        }}
      >
        <MessageSquare /> Message
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Message {customer.name}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="flex gap-2" role="radiogroup" aria-label="How to send">
              <Button
                variant={channel === 'sms' ? 'primary' : 'secondary'}
                size="sm"
                role="radio"
                aria-checked={channel === 'sms'}
                disabled={!canText}
                onClick={() => setChannel('sms')}
              >
                Text
              </Button>
              <Button
                variant={channel === 'email' ? 'primary' : 'secondary'}
                size="sm"
                role="radio"
                aria-checked={channel === 'email'}
                disabled={!canEmail}
                onClick={() => setChannel('email')}
              >
                Email
              </Button>
            </div>
            {!canText ? (
              <p className="text-2xs text-foreground-muted">
                Text isn't available — no number that can receive texts.
              </p>
            ) : null}
            {!canEmail ? (
              <p className="text-2xs text-foreground-muted">
                Email isn't available — no email address on file.
              </p>
            ) : null}
            {channel === 'email' ? (
              <Field label="Subject" required>
                {(props) => (
                  <Input
                    {...props}
                    value={subject}
                    maxLength={200}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                )}
              </Field>
            ) : null}
            <Field
              label="Message"
              required
              hint={
                channel === 'sms'
                  ? `${body.length}/1600 — sent to ${customer.phone}. Their reply will appear under Support tickets.`
                  : `Sent to ${customer.email}. Their reply will appear under Support tickets.`
              }
            >
              {(props) => (
                <Textarea
                  {...props}
                  rows={5}
                  autoFocus
                  maxLength={channel === 'sms' ? 1600 : 10000}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              )}
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => send.mutate()}
              disabled={!ready || send.isPending}
            >
              {send.isPending ? 'Sending…' : channel === 'sms' ? 'Send text' : 'Send email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
